import { useEffect, useState } from 'react';
import type { CustomerListItem } from '@kassa/shared';
import { api, ApiError } from '../api';
import { formatMoney } from '../format';
import { DataTable, HeadButton, NewButton, PageHeader, Pager, SearchPanel, useUrlFilters, type Column } from '../components/ListPage';

interface Props {
  canEdit: boolean;
  onOpen: (id: string) => void;
  onNew: () => void;
  /** Excel-dən idxal (import.excel + customer.edit). */
  onImport?: () => void;
}

const PAGE = 25;
const KEYS = ['q', 'debt', 'page', 'sort'] as const;

// Müştəri siyahısı (KT-99 şablonu): axtarış (ad, telefon, VÖEN), «nisyəsi var» süzgəci, səhifələmə; süzgəc URL-də.
// Balans sistemdən (jurnaldan).
export function Customers({ canEdit, onOpen, onNew, onImport }: Props) {
  const [f, setF] = useUrlFilters(KEYS);
  const [q, setQ] = useState(f.q);
  const [debt, setDebt] = useState(f.debt === '1');
  const [items, setItems] = useState<CustomerListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState<{ customers: number; debtors: number; debt: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const page = Number(f.page) || 1;

  // Yazdıqca axtarış (250 ms gecikmə) — URL-ə də yazılır
  useEffect(() => {
    const t = window.setTimeout(() => {
      if (q.trim() !== f.q) setF({ q: q.trim(), page: '' });
    }, 250);
    return () => window.clearTimeout(t);
  }, [q]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .customers({ q: f.q || undefined, credit: f.debt === '1' ? '1' : undefined, page, limit: PAGE, sort: f.sort || undefined })
      .then((r) => {
        if (cancelled) return;
        setItems(r.items);
        setTotal(r.total);
        setSummary(r.summary ?? null);
      })
      .catch((err: unknown) => !cancelled && setError(err instanceof ApiError ? err.message : 'Siyahı yüklənmədi'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [f.q, f.debt, f.sort, page]);

  const columns: Column<CustomerListItem>[] = [
    {
      key: 'name',
      label: 'Ad',
      sortKey: 'name',
      render: (c) => (
        <button type="button" className="cell-link" onClick={() => onOpen(c.id)}>
          {c.name}
        </button>
      ),
    },
    { key: 'phone', label: 'Telefon', mono: true, minor: true, render: (c) => c.phone ?? '—' },
    { key: 'voen', label: 'VÖEN', mono: true, minor: true, render: (c) => c.voen ?? '—' },
    { key: 'limit', label: 'Nisyə limiti', align: 'right', mono: true, minor: true, render: (c) => (c.creditLimit === null ? '—' : formatMoney(c.creditLimit)) },
    { key: 'debt', label: 'Borc', align: 'right', mono: true, sortKey: 'balance', render: (c) => <span className={c.balance > 0 ? 'bad' : undefined}>{formatMoney(c.balance)}</span> },
    { key: 'st', label: 'Status', minor: true, render: (c) => <span className={c.active ? 'badge badge-ok' : 'badge badge-off'}>{c.active ? 'Aktiv' : 'Deaktiv'}</span> },
  ];

  return (
    <main className="page page-list">
      <PageHeader
        title="Müştərilər"
        actions={
          <>
            {onImport && <HeadButton label="Excel-dən yüklə" onClick={onImport} />}
            {canEdit && <NewButton label="Yeni müştəri" onClick={onNew} />}
          </>
        }
      />

      <div className="list-bar">
        <div className="list-search">
          <input type="search" aria-label="Axtar" placeholder="Ad, telefon və ya VÖEN" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
      </div>

      {summary && (
        <div className="list-kpis" aria-label="Göstəricilər">
          <button type="button" className="list-kpi" onClick={() => setF({ debt: '', page: '' })}>
            <span className="muted small">Müştərilər</span>
            <strong>{summary.customers}</strong>
          </button>
          <button type="button" className="list-kpi is-warn" onClick={() => setF({ debt: '1', page: '' })}>
            <span className="muted small">Nisyəsi olan</span>
            <strong>{summary.debtors}</strong>
          </button>
          <div className="list-kpi is-bad">
            <span className="muted small">Cəmi borc</span>
            <strong>{formatMoney(summary.debt)}</strong>
          </div>
        </div>
      )}

      <SearchPanel
        defaultOpen={f.debt === '1'}
        onSearch={() => setF({ debt: debt ? '1' : '', page: '' })}
        onClear={() => {
          setQ('');
          setDebt(false);
          setF({ q: '', debt: '', page: '' });
        }}
      >
        <label className="check">
          <input type="checkbox" checked={debt} onChange={(e) => setDebt(e.target.checked)} />
          Yalnız nisyəsi olanlar
        </label>
      </SearchPanel>

      <DataTable
        sort={f.sort || 'name'}
        onSort={(v) => setF({ sort: v === 'name' ? '' : v, page: '' })}
        label="Müştərilər"
        columns={columns}
        rows={items}
        rowKey={(c) => c.id}
        onEdit={(c) => onOpen(c.id)}
        loading={loading}
        error={error}
        empty={
          f.q || f.debt ? (
            <p className="muted">Bu süzgəclə müştəri tapılmadı.</p>
          ) : (
            <>
              <p className="muted">Müştəri yoxdur.</p>
              <div className="table-empty-actions">
                {canEdit && (
                  <button type="button" className="btn-sec" onClick={onNew}>
                    İlk müştərini əlavə et
                  </button>
                )}
                {onImport && (
                  <button type="button" className="btn-small" onClick={onImport}>
                    Excel-dən yüklə
                  </button>
                )}
              </div>
            </>
          )
        }
      />
      <Pager page={page} limit={PAGE} total={total} unit="müştəri" onPage={(p) => setF({ page: p > 1 ? String(p) : '' })} />
    </main>
  );
}
